import React from 'react';
import { Link } from 'react-router-dom';
import {
  exhibitionLiveStatus,
  exhibitionStatusLabel,
  exhibitionWithDefaultRsvp,
  formatExhibitionBannerDates,
  formatScheduleDatePill,
} from './exhibitionsDisplay';
import { ExhibitionRsvpButton } from './components/ExhibitionRsvpForm';
import { HOME_COPY_DEFAULTS } from './content/pageCopyDefaults';

function LocationPinIcon() {
  return (
    <svg className="home-next-exhibition__pin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

/**
 * Top-of-home strip: next upcoming show (API returns upcoming sorted by start date ascending).
 */
export default function HomeNextExhibitionBanner({
  upcoming,
  homeCopy = {},
  ctaTo = '/exhibitions',
  ctaLabel: ctaLabelProp,
}) {
  const next = upcoming?.[0];
  if (!next) return null;

  const badgeLabel = homeCopy.next_exhibition_badge || HOME_COPY_DEFAULTS.next_exhibition_badge;
  const ctaLabel = ctaLabelProp || homeCopy.next_exhibition_cta_label || HOME_COPY_DEFAULTS.next_exhibition_cta_label;

  const title = next.title?.trim();
  const venue = String(next.venue || '').trim();
  const cityLine = [next.city, next.country].filter(Boolean).join(', ');
  const where = [venue, cityLine].filter(Boolean).join(' · ');
  const when = formatExhibitionBannerDates(next.event_start, next.event_end);
  const pill = formatScheduleDatePill(next.event_start, next.event_end);
  const status = exhibitionLiveStatus(next);
  const statusLabel = exhibitionStatusLabel(status);
  const show = exhibitionWithDefaultRsvp(next);
  const rsvpOpen =
    (show?.rsvp_enabled || show?.preorder_enabled) &&
    !(show?.rsvp_full && !show?.preorder_enabled);

  const ctaClass = 'home-next-exhibition__cta btn btn-sm';

  let ctaNode;
  if (/^https?:\/\//i.test(ctaTo)) {
    ctaNode = (
      <a href={ctaTo} className={ctaClass} target="_blank" rel="noopener noreferrer">
        {ctaLabel}
      </a>
    );
  } else if (ctaTo.startsWith('#')) {
    ctaNode = (
      <a href={ctaTo} className={ctaClass}>
        {ctaLabel}
      </a>
    );
  } else {
    ctaNode = (
      <Link to={ctaTo} className={ctaClass}>
        {ctaLabel}
      </Link>
    );
  }

  return (
    <aside
      className="home-next-exhibition home-next-exhibition--premium home-next-exhibition--classy"
      aria-label="Next exhibition"
    >
      <div className="home-next-exhibition__inner">
        <div className="home-next-exhibition__accent" aria-hidden="true" />

        <div className="home-next-exhibition__date-pill" aria-hidden="true">
          <time dateTime={next.event_start}>
            <span className="home-next-exhibition__date-month">{pill.month}</span>
            <span className="home-next-exhibition__date-day">{pill.day}</span>
          </time>
        </div>

        <div className="home-next-exhibition__content">
          <div className="home-next-exhibition__labels">
            <span className="home-next-exhibition__badge">{badgeLabel}</span>
            {statusLabel ? (
              <span className={`home-next-exhibition__status home-next-exhibition__status--${status}`}>
                {statusLabel}
              </span>
            ) : null}
          </div>
          <div className="home-next-exhibition__text">
            {title ? <p className="home-next-exhibition__title">{title}</p> : null}
            <p className="home-next-exhibition__when">
              <time dateTime={next.event_start}>{when}</time>
            </p>
            {where ? (
              <p className="home-next-exhibition__where-row">
                <LocationPinIcon />
                <span className="home-next-exhibition__where">{where}</span>
              </p>
            ) : null}
          </div>
        </div>

        <div className="home-next-exhibition__cta-wrap">
          {rsvpOpen ? (
            <ExhibitionRsvpButton row={show} compact className="home-next-exhibition__rsvp" />
          ) : null}
          {ctaNode}
        </div>
      </div>
    </aside>
  );
}
